const normalizeFieldName = (name = '') => {
  if (typeof name !== 'string' || !name) {
    return ''
  }
  return name
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, (_, a, b) => `${a} ${b.toLowerCase()}`)
    .replace(/\s+/g, ' ')
    .trim()
}

const ensureTimestamp = (isoOrTimestamp) => {
  if (!isoOrTimestamp) {
    return null
  }

  const date = new Date(isoOrTimestamp)
  if (!Number.isNaN(date.getTime())) {
    return date.toISOString()
  }

  const isoCandidate = `${isoOrTimestamp}`.trim()
  if (!isoCandidate) {
    return null
  }

  const isoDate = new Date(`${isoCandidate}T00:00:00.000Z`)
  if (Number.isNaN(isoDate.getTime())) {
    return null
  }
  return isoDate.toISOString()
}

const isDateFieldType = (receipt, fieldName) => {
  if (!receipt || !fieldName) {
    return false
  }
  const field = receipt?.data?.[fieldName]
  if (!field || typeof field !== 'object') {
    return false
  }
  const typeValue = field.type || field.fieldType || field?.value?.type
  if (typeof typeValue === 'string') {
    return typeValue.trim().toLowerCase() === 'date'
  }
  if (typeof field?.data === 'object') {
    const nestedType = field.data?.type || field.data?.fieldType
    if (typeof nestedType === 'string') {
      return nestedType.trim().toLowerCase() === 'date'
    }
  }
  return false
}

const extractFieldLabel = (field) => {
  if (!field || typeof field !== 'object') {
    return null
  }

  const labelSources = [
    field.label,
    field.displayName,
    field.name,
    field?.value?.label,
    field?.value?.displayName,
    field?.value?.name,
    field?.data?.label,
    field?.data?.displayName,
    field?.data?.name
  ]

  for (const candidate of labelSources) {
    if (typeof candidate === 'string' && candidate.trim().length > 0) {
      return candidate.trim()
    }
  }

  return null
}

export const resolveReceiptDateDetails = (receipt, options = {}) => {
  const fallbackKey = options.fallbackKey || 'createdAt'

  if (!receipt || typeof receipt !== 'object') {
    return {
      iso: null,
      timestamp: null,
      source: null,
      fieldName: null,
      matchedByFilter: false,
      sourceLabel: null,
      tooltip: null,
    }
  }

  const resolved = receipt?.resolvedDate || null
  const fallbackRaw = receipt?.[fallbackKey] || receipt?.createdAt || null

  let timestamp = null
  let iso = null
  let source = 'createdAt'
  let fieldName = null
  let matchedByFilter = false

  if (resolved && (resolved.timestamp || resolved.iso)) {
    timestamp = ensureTimestamp(resolved.timestamp || resolved.iso)
    if (timestamp) {
      iso = timestamp.split('T')[0]
    } else if (resolved.iso) {
      iso = resolved.iso
      const isoDate = new Date(`${resolved.iso}T00:00:00.000Z`)
      timestamp = Number.isNaN(isoDate.getTime()) ? null : isoDate.toISOString()
    }
    source = resolved.source || source
    fieldName = resolved.fieldName || null
    matchedByFilter = Boolean(resolved.matchedByFilter)
  }

  const fieldIsDateType = source === 'field' && fieldName && isDateFieldType(receipt, fieldName)
  if (source === 'field' && (!fieldName || !fieldIsDateType)) {
    source = 'createdAt'
    fieldName = null
    timestamp = null
    iso = null
    matchedByFilter = false
  }

  if (!timestamp && fallbackRaw) {
    const fallbackDate = ensureTimestamp(fallbackRaw)
    if (fallbackDate) {
      timestamp = fallbackDate
      iso = fallbackDate.split('T')[0]
    }
  }

  const readableField = fieldName ? normalizeFieldName(fieldName) : null
  const fieldLabel = fieldName ? extractFieldLabel(receipt?.data?.[fieldName]) : null
  const displayLabel = fieldLabel || readableField

  let sourceLabel = null
  if (source === 'field') {
    sourceLabel = displayLabel ? `Feld "${displayLabel}"` : 'Feld-Datum'
  } else if (source === 'createdAt') {
    sourceLabel = 'Erstelldatum'
  } else {
    sourceLabel = 'Datum'
  }

  let tooltip = null
  if (source === 'field') {
    tooltip = displayLabel
      ? `Datum aus Feld "${displayLabel}"`
      : 'Datum aus einem benutzerdefinierten Feld'
  } else if (source === 'createdAt') {
    tooltip = 'Erstelldatum der Quittung'
  }

  return {
    iso,
    timestamp,
      source,
      fieldName,
      matchedByFilter,
      sourceLabel,
      tooltip,
    }
  }

export const getReceiptDateValue = (receipt, options = {}) => {
  const details = resolveReceiptDateDetails(receipt, options)
  return details.timestamp || details.iso || null
}
